import {track} from "@amplitude/analytics-browser";
import {useQuery} from "@apollo/client";
import {
  faCalendarDays,
  faExclamationTriangle,
  faFilter,
  faLock,
  faMoon,
  faPeopleGroup,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  Button,
  Center,
  Checkbox,
  CloseButton,
  Fieldset,
  NumberInput,
  SimpleGrid,
  Space,
  Stack,
  Tooltip,
} from "@mantine/core";
import {DatePickerInput, DatesProvider} from "@mantine/dates";
import classNames from "classnames";
import dayjs from "dayjs";
import {flatten as flattenObj} from "flat";
import {Formik, FormikErrors, getIn, setIn} from "formik";
import {isEmpty, isEqual, noop, pick} from "lodash";
import Link from "next/link";
import React, {useEffect, useState} from "react";

import classes from "./FilterPanel.module.scss";
import * as queries from "../../../graphql/queries";
import type {MapSearchInput} from "../../../graphql/types";
import {DEFAULT_SEARCH_INPUT} from "../../../lib/search";
import {blanksAsNulls, omitNil} from "../../../lib/utils";
import {useAuth} from "../../contexts/AuthContext";
import HelpLink from "../../HelpLink";

// Filters handled by this control
export const HANDLED_FILTERS: Readonly<Array<string | string[]>> = [
  ["filter", "dates", "min"],
  ["filter", "dates", "max"],
  ["filter", "excludeFCFS"],
  ["filter", "excludeGroup"],
  ["filter", "excludeMandatoryEquipment"],
  ["filter", "excludeUnreleased"],
  ["filter", "isStrict"],
  ["filter", "maxVehicleLengthFeet"],
  ["filter", "nights"],
  ["filter", "onlyAccessible"],
  ["filter", "onlyFiresAllowed"],
  ["filter", "onlyPetsAllowed"],
  ["filter", "onlyFirePit"],
  ["filter", "onlyPicnicTable"],
  ["filter", "onlyWaterHookup"],
  ["filter", "onlySewerHookup"],
  ["filter", "people", "min"],
  ["filter", "people", "max"],
  ["filter", "vehicles"],
  "onlyMatches",
];

export interface FilterPanelProps {
  className?: string;
  initialFilters: Partial<MapSearchInput>; // Initial filters
  searching: boolean; // Is search currently executing? If so, disables inputs
  onChange?(filters: Partial<MapSearchInput>): void; // Callback to invoke when filters changed
}

/**
 * Search filters
 */
export default function FilterPanel({
  className,
  searching = false,
  onChange = noop,
  initialFilters = DEFAULT_SEARCH_INPUT,
}: FilterPanelProps) {
  const {authentication} = useAuth();
  const [isFiltering, setFiltering] = useState<boolean>(false);
  // const {data: {search: {maxDate = undefined} = {}} = {}} = useQuery(
  //   queries.GET_MAX_DATE
  // );
  const {data: {features: {isEnabled: filterGate = undefined} = {}} = {}} =
    useQuery(queries.GET_FEATURE_FLAG, {
      variables: {flag: "FILTER_GATE"},
    });

  const today = dayjs();
  const dateFormat = "YYYY-MM-DD";

  /*
  Minimum value for date inputs

  Workaround: Subtracting 1 day because Chrome and Firefox's built-in date picker seems to disable the current
  day towards the later part of the day in production, but it works fine locally. Must be something to do with
  time zones somewhere, but I'm unsure what's causing the behavior. Creating a `new Date()` on each site seems
  to return the same time zone.
  */
  // const minDate: string = dayjs().subtract(1, "days").format("YYYY-MM-DD");

  useEffect(() => {
    if (isFiltering && !searching) {
      setFiltering(false);
    }
  }, [searching]);

  return (
    <div className={classNames(classes.filterPanel, className)}>
      <h2>Filter</h2>
      <Formik<Partial<MapSearchInput>>
        initialValues={initialFilters}
        validate={values => {
          let errors: FormikErrors<Partial<MapSearchInput>> = {};

          // Validate date ranges and dependent fields
          const filterMinDate = getIn(values, "filter.dates.min");
          const filterMaxDate = getIn(values, "filter.dates.max");
          const filterNights = getIn(values, "filter.nights");

          if (filterMinDate || filterMaxDate) {
            if (!filterMinDate || !filterMaxDate) {
              errors = setIn(
                errors,
                "filter.dates.min",
                "Both begin and end dates must be entered together"
              );
            } else {
              const beginDate = dayjs(filterMinDate, dateFormat);
              const endDate = dayjs(filterMaxDate, dateFormat);
              // const maxDateObj = maxDate ? dayjs(maxDate, dateFormat) : null;

              // today <= dates.min
              if (beginDate.isBefore(today.startOf("day"))) {
                errors = setIn(
                  errors,
                  "filter.dates.min",
                  `Begin date is in the past: ${beginDate.format("L")}`
                );
              }

              // Validate validate dates.max <= max(slots.date)
              // if (maxDateObj && endDate.isAfter(maxDateObj)) {
              //   errors = setIn(
              //     errors,
              //     "filter.dates.min",
              //     `We don't have reservation data beyond ${maxDateObj.format(
              //       "L"
              //     )}`
              //   );
              // }

              // dates.min <= dates.max
              if (endDate.isSameOrAfter(beginDate)) {
                // nights is agreeable with date range
                if (
                  filterNights &&
                  beginDate.add(filterNights - 1, "days").isAfter(endDate)
                ) {
                  errors = setIn(
                    errors,
                    "filter.nights",
                    "Number of nights is more than your selected date range"
                  );
                }
              } else {
                errors = setIn(
                  errors,
                  "filter.dates.min",
                  "End date must be greater than or the same as begin date"
                );
              }
            }

            //if (!filterNights) {
            //  errors = setIn(errors, "filter.nights", "Nights are required when dates are entered");
            //}
          } else {
            if (filterNights) {
              errors = setIn(
                errors,
                "filter.dates.min",
                "Dates are required when nights are entered"
              );
            }
          }

          // Validate nights
          if (filterNights) {
            // TODO: Validate nights is valid between dates.min and dates.max

            if (filterNights < 1) {
              errors = setIn(
                errors,
                "filter.nights",
                "Number of nights must be at least 1"
              );
            }
          }

          // Validate people
          const filterPeople = getIn(values, "filter.people.max");
          if (filterPeople && filterPeople < 1) {
            errors = setIn(
              errors,
              "filter.people.max",
              "Group size must be at least 1"
            );
          }

          return errors;
        }}
        onSubmit={async (values, {setSubmitting}) => {
          const submitValues = blanksAsNulls(pick(values, HANDLED_FILTERS));
          track("Search Filtered", {
            filters: submitValues,
          });
          setFiltering(true);
          onChange(submitValues);
          setSubmitting(false);
        }}>
        {({
          handleSubmit,
          values,
          errors,
          isValidating,
          isSubmitting,
          submitCount,
          setFieldValue,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <Stack>
              <DatesProvider settings={{firstDayOfWeek: 0}}>
                <Tooltip
                  withArrow
                  label="Dates you are looking for a camp site">
                  <DatePickerInput
                    type="range"
                    label="Trip dates"
                    numberOfColumns={2}
                    error={submitCount > 0 && getIn(errors, "filter.dates.min")}
                    defaultDate={today.toDate()}
                    excludeDate={date => {
                      const dateObj = dayjs(date);
                      if (dateObj.isBefore(today.startOf("day"))) {
                        return true;
                      }

                      // if (maxDate) {
                      //   const maxDateObj = dayjs(maxDate, dateFormat);
                      //   return dateObj.isAfter(maxDateObj);
                      // }

                      return false;
                    }}
                    valueFormat="L"
                    value={[
                      getIn(values, "filter.dates.min")
                        ? dayjs(
                            getIn(values, "filter.dates.min"),
                            dateFormat
                          ).toDate()
                        : null,
                      getIn(values, "filter.dates.max")
                        ? dayjs(
                            getIn(values, "filter.dates.max"),
                            dateFormat
                          ).toDate()
                        : null,
                    ]}
                    onChange={value => {
                      const [beginDate, endDate] = value;
                      setFieldValue(
                        "filter.dates.min",
                        beginDate ? dayjs(beginDate).format(dateFormat) : null
                      );
                      setFieldValue(
                        "filter.dates.max",
                        endDate ? dayjs(endDate).format(dateFormat) : null
                      );
                      setFieldValue(
                        "filter.nights",
                        dayjs(endDate).diff(dayjs(beginDate), "day")
                      );
                    }}
                    clearable
                    leftSection={<FontAwesomeIcon icon={faCalendarDays} />}
                    leftSectionPointerEvents="none"
                  />
                </Tooltip>
              </DatesProvider>

              <SimpleGrid cols={2}>
                <Tooltip
                  withArrow
                  label="Minimum number of consecutive nights you want to stay">
                  <NumberInput
                    name="filter.nights"
                    label="Minimum nights to stay"
                    error={submitCount > 0 && getIn(errors, "filter.nights")}
                    min={1}
                    value={getIn(values, "filter.nights") || ""}
                    onChange={value => setFieldValue("filter.nights", value)}
                    leftSection={
                      !!getIn(values, "filter.nights") ? (
                        <CloseButton
                          aria-label="Clear input"
                          onClick={() => setFieldValue("filter.nights", "")}
                          style={{
                            display: !!getIn(values, "filter.nights")
                              ? undefined
                              : "none",
                          }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faMoon} />
                      )
                    }
                  />
                </Tooltip>

                <Tooltip
                  withArrow
                  label="Maximum number of people in your group (including children)">
                  <NumberInput
                    name="filter.people.max"
                    label="People in your group"
                    error={
                      submitCount > 0 && getIn(errors, "filter.people.max")
                    }
                    min={1}
                    value={getIn(values, "filter.people.max") || ""}
                    onChange={value =>
                      setFieldValue("filter.people.max", value)
                    }
                    leftSection={
                      !!getIn(values, "filter.people.max") ? (
                        <CloseButton
                          aria-label="Clear input"
                          onClick={() => setFieldValue("filter.people.max", "")}
                          style={{
                            display: !!getIn(values, "filter.people.max")
                              ? undefined
                              : "none",
                          }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faPeopleGroup} />
                      )
                    }
                  />
                </Tooltip>
              </SimpleGrid>

              <Fieldset legend="Only Include">
                <Tooltip
                  withArrow
                  label="Only include camp sites that are accessible to those with disabilities">
                  <Checkbox
                    name="filter.onlyAccessible"
                    label="ADA accessible"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlyAccessible")
                    }
                    checked={getIn(values, "filter.onlyAccessible") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.onlyAccessible",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Only include camp sites that are pet-friendly">
                  <Checkbox
                    name="filter.onlyPetsAllowed"
                    label="Pets allowed"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlyPetsAllowed")
                    }
                    checked={getIn(values, "filter.onlyPetsAllowed") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.onlyPetsAllowed",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                {/*
                <Checkbox
                  name="filter.onlyFiresAllowed"
                  label="Fires allowed"
                  disabled
                />
                <Space h="sm" />
                */}
                <Tooltip
                  withArrow
                  label="Only include camp sites with fire pit/ring">
                  <Checkbox
                    name="filter.onlyFirePit"
                    label="Fire pit"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlyFirePit")
                    }
                    checked={getIn(values, "filter.onlyFirePit") || false}
                    onChange={event =>
                      setFieldValue("filter.onlyFirePit", event.target.checked)
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Only include camp sites with a picnic table">
                  <Checkbox
                    name="filter.onlyPicnicTable"
                    label="Picnic table"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlyPicnicTable")
                    }
                    checked={getIn(values, "filter.onlyPicnicTable") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.onlyPicnicTable",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Only include camp sites with a water hookup">
                  <Checkbox
                    name="filter.onlyWaterHookup"
                    label="Water hookup"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlyWaterHookup")
                    }
                    checked={getIn(values, "filter.onlyWaterHookup") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.onlyWaterHookup",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Only include camp sites with a sewer hookup">
                  <Checkbox
                    name="filter.onlySewerHookup"
                    label="Sewer hookup"
                    error={
                      submitCount > 0 && getIn(errors, "filter.onlySewerHookup")
                    }
                    checked={getIn(values, "filter.onlySewerHookup") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.onlySewerHookup",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
              </Fieldset>
              <Fieldset legend="Exclude">
                <Tooltip
                  withArrow
                  label="Exclude group camp sites. These are usually more expensive sites that can host larger groups">
                  <Checkbox
                    name="filter.excludeGroup"
                    label="Group camp sites"
                    error={
                      submitCount > 0 && getIn(errors, "filter.excludeGroup")
                    }
                    checked={getIn(values, "filter.excludeGroup") || false}
                    onChange={event =>
                      setFieldValue("filter.excludeGroup", event.target.checked)
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Exclude RV-only camp sites (sites that require an RV)">
                  <Checkbox
                    name="filter.excludeMandatoryEquipment"
                    label="RV-only camp sites"
                    error={
                      submitCount > 0 &&
                      getIn(errors, "filter.excludeMandatoryEquipment")
                    }
                    checked={
                      getIn(values, "filter.excludeMandatoryEquipment") || false
                    }
                    onChange={event =>
                      setFieldValue(
                        "filter.excludeMandatoryEquipment",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Exclude camp sites that can't be reserved in advance (e.g. walk-in only, first come first served)">
                  <Checkbox
                    name="filter.excludeFCFS"
                    label="Sites that can't be reserved"
                    error={
                      submitCount > 0 && getIn(errors, "filter.excludeFCFS")
                    }
                    checked={getIn(values, "filter.excludeFCFS") || false}
                    onChange={event =>
                      setFieldValue("filter.excludeFCFS", event.target.checked)
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Exclude camp sites that had canceled reservations, but haven't been released yet">
                  <Checkbox
                    name="filter.excludeUnreleased"
                    label="Unreleased camp sites"
                    error={
                      submitCount > 0 &&
                      getIn(errors, "filter.excludeUnreleased")
                    }
                    checked={getIn(values, "filter.excludeUnreleased") || false}
                    onChange={event =>
                      setFieldValue(
                        "filter.excludeUnreleased",
                        event.target.checked
                      )
                    }
                  />
                </Tooltip>
              </Fieldset>

              <Fieldset legend="Options">
                <Tooltip
                  withArrow
                  label={
                    <div>
                      <strong>Strictly match search criteria</strong>
                      <div>
                        If camp sites are missing data for the specific criteria
                        you are searching for, they will be excluded as if they
                        did not match your search criteria at all.
                      </div>
                    </div>
                  }>
                  <Checkbox
                    name="filter.isStrict"
                    label="Strict matching"
                    error={submitCount > 0 && getIn(errors, "filter.isStrict")}
                    checked={getIn(values, "filter.isStrict") || false}
                    onChange={event =>
                      setFieldValue("filter.isStrict", event.target.checked)
                    }
                  />
                </Tooltip>
                <Space h="sm" />
                <Tooltip
                  withArrow
                  label="Only show matching results on the map rather than showing non-matching results as gray map markers">
                  <Checkbox
                    name="onlyMatches"
                    label="Hide results that don't match"
                    error={submitCount > 0 && errors.onlyMatches}
                    checked={values.onlyMatches || false}
                    onChange={event =>
                      setFieldValue("onlyMatches", event.target.checked)
                    }
                  />
                </Tooltip>
              </Fieldset>

              <div className={classes.searchButtonContainer}>
                <Button.Group>
                  <Tooltip
                    withArrow
                    label={
                      submitCount === 0 || isEmpty(errors)
                        ? "Search"
                        : "Please correct errors"
                    }
                    color={
                      submitCount === 0 || isEmpty(errors) ? undefined : "red"
                    }>
                    <Button
                      variant="primary"
                      onClick={() => handleSubmit()}
                      disabled={isValidating || isSubmitting || isFiltering}
                      loading={isFiltering}
                      leftSection={
                        !isFiltering && !isEmpty(errors) && submitCount > 0 ? (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            beatFade
                          />
                        ) : (
                          <FontAwesomeIcon icon={faFilter} />
                        )
                      }>
                      Apply
                    </Button>
                  </Tooltip>
                  <Button
                    variant="default"
                    onClick={() => {
                      resetForm({values: DEFAULT_SEARCH_INPUT});
                      handleSubmit();
                    }}>
                    Clear
                  </Button>
                </Button.Group>
              </div>
            </Stack>

            {!!filterGate && authentication.status !== "SIGNED_IN" && (
              <div className={classes.signinNag}>
                <div>
                  <FontAwesomeIcon icon={faLock} />{" "}
                  <Link href="/signIn">Sign in</Link> or{" "}
                  <Link href="/signUp">sign up</Link> to use filters
                </div>
              </div>
            )}

            <Space h="xl" />

            <Center>
              <HelpLink slug="filters" title="Need help?" />
            </Center>
          </form>
        )}
      </Formik>
    </div>
  );
}

/**
 * Determine if user applied any filters with the FilterPanel component
 *
 * @param searchInput Input to check
 * @return Whether or not the input had any filters set by this component
 */
export function areFiltersApplied(
  searchInput: Partial<MapSearchInput> | undefined
): boolean {
  if (!searchInput) {
    return false;
  }

  const actualFilters = omitNil(
    flattenObj(pick(searchInput || {}, HANDLED_FILTERS) || {})
  );
  const emptyFilters = omitNil(flattenObj(DEFAULT_SEARCH_INPUT)) || {};
  const areFiltersEmpty =
    isEmpty(actualFilters) || isEqual(actualFilters, emptyFilters);

  return !areFiltersEmpty;
}
